import React, { useState, useEffect } from 'react';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Download, FileText, CheckCircle, AlertCircle, Loader2, X, Calendar } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNotifications } from '../store/useStore';

interface ExportInvoicesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface InvoiceData {
  id: string;
  invoice_number: string | null;
  document_type: string;
  customer_id: string;
  customer_name: string;
  status: string;
  invoice_date: string;
  total_amount: number;
  tax_amount: number;
  notes?: string;
  items: Array<{
    product_code: string | null;
    product_name: string;
    quantity: number;
    unit_price: number;
    line_total: number;
    unit: string | null;
  }>;
}

const ExportInvoicesModal: React.FC<ExportInvoicesModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [isExporting, setIsExporting] = useState(false);
  const [customers, setCustomers] = useState<Array<{id: string, company_name: string}>>([]);
  const [exportResults, setExportResults] = useState<{
    success: boolean;
    count: number;
    error?: string;
  }>({ success: false, count: 0 });
  const { addNotification } = useNotifications();

  useEffect(() => {
    if (isOpen) {
      loadCustomers();
      // Imposta date di default (ultimo mese)
      const today = new Date();
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
      setDateFrom(lastMonth.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, [isOpen]);

  const loadCustomers = async () => {
    try {
      const { data: customersData } = await supabase
        .from('customers')
        .select('id, company_name')
        .eq('is_active', true)
        .order('company_name');

      setCustomers(customersData || []);
    } catch (error) {
      console.error('Errore nel caricamento clienti:', error);
    }
  };

  const handleExport = async () => {
    if (!dateFrom || !dateTo) {
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Seleziona le date di inizio e fine'
      });
      return;
    }

    setIsExporting(true);
    setExportResults({ success: false, count: 0 });

    try {
      // Costruisci query per le fatture
      let query = supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          document_type,
          customer_id,
          status,
          invoice_date,
          total_amount,
          tax_amount,
          notes,
          customers!inner(company_name),
          invoice_items(
            description,
            quantity,
            unit_price,
            line_total,
            unit,
            products(code, name)
          )
        `)
        .gte('invoice_date', dateFrom)
        .lte('invoice_date', dateTo);

      // Applica filtri
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (typeFilter !== 'all') {
        query = query.eq('document_type', typeFilter);
      }

      if (customerFilter !== 'all') {
        query = query.eq('customer_id', customerFilter);
      }

      const { data: invoicesData, error } = await query.order('invoice_date', { ascending: false });

      if (error) throw error;

      if (!invoicesData || invoicesData.length === 0) {
        setExportResults({ success: true, count: 0 });
        addNotification({
          type: 'info',
          title: 'Info',
          message: 'Nessun documento trovato con i filtri selezionati'
        });
        return;
      }

      // Prepara dati per CSV
      const csvData = prepareCSVData(invoicesData as any);
      
      // Genera e scarica CSV
      downloadCSV(csvData, `documenti_${dateFrom}_${dateTo}.csv`);
      
      setExportResults({ success: true, count: invoicesData.length });
      addNotification({
        type: 'success',
        title: 'Successo',
        message: `${invoicesData.length} documenti esportati con successo`
      });

    } catch (error) {
      console.error('Errore durante l\'esportazione:', error);
      setExportResults({ 
        success: false, 
        count: 0, 
        error: error instanceof Error ? error.message : 'Errore sconosciuto' 
      });
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore durante l\'esportazione'
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getDocumentTypeLabel = (type: string): string => {
    switch (type) {
      case 'invoice': return 'Fattura';
      case 'invoice_with_delivery': return 'Fattura Accompagnatoria';
      case 'proforma': return 'Fattura Pro Forma';
      case 'ddt': return 'DDT';
      case 'credit_note': return 'Nota di Credito';
      default: return type;
    }
  };

  const prepareCSVData = (invoices: any[]): string[][] => {
    const headers = [
      'ID Documento',
      'Numero Documento',
      'Tipo Documento',
      'Cliente',
      'Stato',
      'Data Documento',
      'Totale',
      'IVA',
      'Note',
      'Codice Prodotto',
      'Descrizione Prodotto',
      'Quantità',
      'Prezzo Unitario',
      'Totale Riga',
      'Unità'
    ];

    const rows: string[][] = [headers];

    invoices.forEach(invoice => {
      if (invoice.invoice_items && invoice.invoice_items.length > 0) {
        invoice.invoice_items.forEach((item: any) => {
          rows.push([
            invoice.id,
            invoice.invoice_number || 'Bozza',
            getDocumentTypeLabel(invoice.document_type || 'invoice'),
            invoice.customers?.company_name || '',
            invoice.status === 'issued' ? 'Emessa' : invoice.status === 'draft' ? 'Bozza' : 'Annullata',
            new Date(invoice.invoice_date).toLocaleDateString('it-IT'),
            invoice.total_amount?.toString() || '0',
            invoice.tax_amount?.toString() || '0',
            invoice.notes || '',
            item.products?.code || '',
            item.description || item.products?.name || '',
            item.quantity?.toString() || '0',
            item.unit_price?.toString() || '0',
            item.line_total?.toString() || '0',
            item.unit || ''
          ]);
        });
      } else {
        // Documento senza items
        rows.push([
          invoice.id,
          invoice.invoice_number || 'Bozza',
          getDocumentTypeLabel(invoice.document_type || 'invoice'),
          invoice.customers?.company_name || '',
          invoice.status === 'issued' ? 'Emessa' : invoice.status === 'draft' ? 'Bozza' : 'Annullata',
          new Date(invoice.invoice_date).toLocaleDateString('it-IT'),
          invoice.total_amount?.toString() || '0',
          invoice.tax_amount?.toString() || '0',
          invoice.notes || '',
          '',
          '',
          '',
          '',
          '',
          ''
        ]);
      }
    });

    return rows;
  };

  const downloadCSV = (data: string[][], filename: string) => {
    const csvContent = data.map(row => 
      row.map(cell => `"${cell.toString().replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setDateFrom('');
    setDateTo('');
    setStatusFilter('all');
    setTypeFilter('all');
    setCustomerFilter('all');
    setExportResults({ success: false, count: 0 });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="flex flex-col gap-3 overflow-hidden sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-6 h-6 text-blue-600" />
            <span>Esporta Documenti in CSV</span>
          </DialogTitle>
          <DialogDescription>
            Seleziona i filtri per esportare i documenti in formato CSV.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-6 px-1">
          {/* Date Range */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Periodo
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label htmlFor="dateFrom">Data Inizio</Label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="dateTo">Data Fine</Label>
                <Input
                  id="dateTo"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Filtri
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label htmlFor="statusFilter">Stato Documento</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli stati</SelectItem>
                    <SelectItem value="draft">Bozza</SelectItem>
                    <SelectItem value="issued">Emessa</SelectItem>
                    <SelectItem value="cancelled">Annullata</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="typeFilter">Tipo Documento</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i tipi</SelectItem>
                    <SelectItem value="invoice">Fattura</SelectItem>
                    <SelectItem value="invoice_with_delivery">Fattura Accompagnatoria</SelectItem>
                    <SelectItem value="proforma">Fattura Pro Forma</SelectItem>
                    <SelectItem value="ddt">DDT</SelectItem>
                    <SelectItem value="credit_note">Nota di Credito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="customerFilter">Cliente</Label>
                <Select value={customerFilter} onValueChange={setCustomerFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i clienti</SelectItem>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Export Results */}
          {exportResults.success && exportResults.count > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-2 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span>{exportResults.count} documenti esportati con successo</span>
              </div>
            </div>
          )}

          {exportResults.success && exportResults.count === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2 text-yellow-600">
                <AlertCircle className="w-4 h-4" />
                <span>Nessun documento trovato con i filtri selezionati</span>
              </div>
            </div>
          )}

          {!exportResults.success && exportResults.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-2 text-red-600">
                <AlertCircle className="w-4 h-4" />
                <span className="break-words">Errore: {exportResults.error}</span>
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isExporting}>
            <X className="w-4 h-4 mr-2" />
            Chiudi
          </Button>
          <Button
            onClick={handleExport}
            disabled={!dateFrom || !dateTo || isExporting}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Esporta CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportInvoicesModal;
